import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Activity, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

// Handles the Emergent Google OAuth return: exchanges #session_id for an app session.
export default function AuthCallback() {
  const { completeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return; // guard against StrictMode double-invoke
    processed.current = true;
    const m = (window.location.hash || "").match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    completeGoogleSession(sessionId)
      .then(() => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      })
      .catch(() => setError("Google sign-in could not be completed. Please try again."));
  }, [completeGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7F9]" data-testid="auth-callback">
      {error ? (
        <div className="text-center max-w-sm px-6">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
          <p className="text-sm text-slate-700 font-medium">{error}</p>
          <Button onClick={() => navigate("/login", { replace: true })} className="mt-4 bg-[#0047AB] hover:bg-[#003380] text-white" data-testid="auth-callback-retry">
            Back to sign in
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Activity className="w-4 h-4 animate-pulse" /> Signing you in…
        </div>
      )}
    </div>
  );
}
